/**
 * Admin → Invoices → Import transactions (server: game/admin/transaction-import/).
 *
 * Upload → the server validates (dry run) and returns every row normalised plus its errors →
 * the admin edits cells → RE-CHECK sends the edited rows through the same server validation →
 * IMPORT sends them once more; the server re-validates and writes all-or-nothing. This panel
 * only shows what the last check said — the server is the only validator. A full card number
 * typed into a cell is refused by the server and comes back empty with a row error.
 *
 * Port of Dominion's ImportTransactionsPanel, in this panel's own look (ui.ts helpers).
 */
import { el, clear, toast } from '../../dom';
import { ApiError } from '../../../core/network/api';
import {
  IMPORT_FIELDS,
  adminApi,
  type ImportField,
  type ImportItem,
  type ImportReport,
  type ImportRowBody,
} from '../../game-api';
import { apiMessage, btn, saveBlob, toastError } from './ui';

export type EditRow = { row: number; values: Record<ImportField, string> };

const LABELS: Record<ImportField, string> = {
  invoiceNumber: 'Invoice #', email: 'Email', username: 'Username', amount: 'Amount', currency: 'Cur.',
  type: 'Type', status: 'Status', purpose: 'Purpose', paymentMethod: 'Method', maskedPan: 'Card',
  paymentDetails: 'Payment details', date: 'Date (UTC)', time: 'Time', vatRate: 'VAT', shrooms: 'Shrooms',
  transactionId: 'Transaction ID',
};
/** Column widths in px, sized for the longest value admins usually type. */
const WIDTHS: Record<ImportField, number> = {
  invoiceNumber: 130, email: 190, username: 120, amount: 80, currency: 60, type: 104, status: 96,
  purpose: 180, paymentMethod: 140, maskedPan: 160, paymentDetails: 200, date: 104, time: 64, vatRate: 60, shrooms: 84, transactionId: 130,
};
const CHOICES: Partial<Record<ImportField, string[]>> = { type: ['incoming', 'outgoing'], status: ['', 'paid', 'not paid'] };

export const TEMPLATE_FILENAME = 'transactions-import-template.xlsx';

/** The report's normalised values as editable strings (null — unreadable or refused — becomes empty). */
export function toEditRows(items: ImportItem[]): EditRow[] {
  return items.map((i) => ({
    row: i.row,
    values: Object.fromEntries(IMPORT_FIELDS.map((f) => [f, i.values[f] == null ? '' : String(i.values[f])])) as Record<ImportField, string>,
  }));
}

/** Server errors name the field ("amount is not a number", "maskedPan: …", "invalid email"). */
export function fieldHasError(errors: string[], field: ImportField): boolean {
  return errors.some((e) => new RegExp(`(^|\\s)${field}\\b`).test(e));
}

/** The body RE-CHECK / IMPORT send: each row as typed. */
export function toRowBodies(rows: EditRow[]): ImportRowBody[] {
  return rows.map((r) => ({ row: r.row, ...r.values }));
}

/** The report a refused call carried (400 on row errors), if any. */
function rejectedReport(e: unknown): ImportReport | null {
  const data = e instanceof ApiError ? e.details : null;
  return data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items) ? data as ImportReport : null;
}

/** What to say about a failed call. No answer at all is NOT "nothing was imported": it may have been. */
export function importFailure(e: unknown): string {
  if (e instanceof ApiError && e.status === 0) {
    return 'No answer from the server — the import may still have been written. Check the invoices list before importing again.';
  }
  return apiMessage(e, 'Import failed');
}

const cellCls = (bad: boolean) =>
  `px-1.5 py-1 rounded bg-surface-2 border text-xs text-text outline-none focus:border-gold ${bad ? 'border-error' : 'border-border'}`;

/**
 * The import panel. `onImported` runs after a successful import (reload the invoices list);
 * `onClose` when the admin cancels without a file loaded.
 */
export function importPanel(onImported: () => void, onClose: () => void): HTMLElement {
  let source = '';
  let report: ImportReport | null = null;
  let rows: EditRow[] = [];
  let dirty = false;
  let busy = false;

  const file = el('input', 'text-xs text-text-dim max-w-full', { type: 'file', accept: '.xlsx,.csv' }) as HTMLInputElement;
  file.setAttribute('aria-label', 'Spreadsheet to import');
  const status = el('span', 'text-xs text-text-dim');
  const message = el('div', 'hidden game-font rounded-lg border-2 border-[#8a3b2e] bg-[#2a1512] p-3 text-sm text-[#ffb3a7]');
  const body = el('div', 'flex flex-col gap-3');

  const recheck = btn('RE-CHECK', () => void run(() => adminApi.importRows(toRowBodies(rows), source, true), false));
  const doImport = btn('IMPORT', () => void run(() => adminApi.importRows(toRowBodies(rows), source, false), true), 'primary');
  const cancel = btn('CANCEL', () => reset());

  function syncButtons() {
    recheck.disabled = busy || !dirty;
    doImport.disabled = busy || dirty || !report?.valid;
    doImport.textContent = busy ? 'WORKING…' : `IMPORT ${report?.rows ?? 0} TRANSACTIONS`;
    status.textContent = busy ? 'Checking…' : '';
    for (const b of [recheck, doImport]) b.classList.toggle('opacity-40', b.disabled);
  }

  function showMessage(text: string | null) {
    message.textContent = text ?? '';
    message.classList.toggle('hidden', !text);
  }

  function reset() {
    source = ''; report = null; rows = []; dirty = false;
    file.value = '';
    showMessage(null);
    draw();
  }

  async function run(call: () => Promise<ImportReport>, commit: boolean) {
    busy = true;
    showMessage(null);
    syncButtons();
    try {
      const r = await call();
      if (commit) {
        const created = r.playersCreated?.length ?? 0;
        toast(`Imported ${r.imported ?? 0} transaction(s)${created ? `, ${created} new player(s)` : ''}`);
        busy = false;
        reset();
        onImported();
        return;
      }
      report = r; rows = toEditRows(r.items); dirty = false;
    } catch (e) {
      const rejected = rejectedReport(e);
      if (rejected) { report = rejected; rows = toEditRows(rejected.items); dirty = false; }
      if (!(e instanceof ApiError)) toastError(e, 'Import failed');
      showMessage(importFailure(e));
    } finally {
      busy = false;
    }
    draw();
  }

  file.addEventListener('change', () => {
    const f = file.files?.[0];
    report = null; rows = []; dirty = false;
    showMessage(null);
    draw();
    if (f) { source = f.name; void run(() => adminApi.importPreviewFile(f), false); }
  });

  function summary(r: ImportReport): HTMLElement {
    const created = r.playersToCreate;
    return el('div', 'text-sm text-text', {},
      `${r.rows} rows (${r.incoming} incoming, ${r.outgoing} outgoing) · ${r.playersMatched} existing players · `,
      el('span', created.length ? 'text-gold' : '', {}, `${created.length} new`),
      created.length ? el('span', 'text-text-dim', {}, ` (${created.slice(0, 5).join(', ')}${created.length > 5 ? ', …' : ''})`) : null,
      ` · blank Shrooms = EUR amount × ${r.shroomsPerEur}`,
      r.totalErrors > 0 ? el('span', 'text-error font-bold', {}, ` · ${r.totalErrors} error(s): fix the highlighted cells and re-check`) : null,
      el('span', `text-gold font-bold ${dirty ? '' : 'hidden'}`, { dataset: { role: 'edited' } }, ' · edited: re-check before importing'));
  }

  function rowNodes(r: EditRow, item: ImportItem | undefined): HTMLElement[] {
    const errors = item?.errors ?? [];
    const p = item?.preview;
    // Blank cells show what the server will use.
    const placeholder: Partial<Record<ImportField, string>> = {
      invoiceNumber: '(auto)', currency: 'EUR', shrooms: p ? String(p.shrooms) : '', username: p?.username ?? '', purpose: p?.purpose ?? '',
    };
    const edit = (f: ImportField, v: string) => {
      r.values[f] = v;
      if (!dirty) {
        dirty = true;
        body.querySelector('[data-role="edited"]')?.classList.remove('hidden');
      }
      syncButtons();
    };
    const cells = IMPORT_FIELDS.map((f) => {
      const bad = fieldHasError(errors, f);
      const choices = CHOICES[f];
      let control: HTMLElement;
      if (choices) {
        const sel = el('select', cellCls(bad), { style: `width:${WIDTHS[f]}px` }) as HTMLSelectElement;
        if (!choices.includes(r.values[f])) sel.appendChild(el('option', '', { value: r.values[f] }, r.values[f] || '—'));
        for (const o of choices) sel.appendChild(el('option', '', { value: o }, o || '(paid)'));
        sel.value = r.values[f];
        sel.setAttribute('aria-label', `Row ${r.row} ${LABELS[f]}`);
        sel.addEventListener('change', () => edit(f, sel.value));
        control = sel;
      } else {
        const inp = el('input', `${cellCls(bad)}${f === 'maskedPan' || f === 'invoiceNumber' ? ' font-mono' : ''}`, {
          value: r.values[f], placeholder: placeholder[f] ?? '', style: `width:${WIDTHS[f]}px`,
        }) as HTMLInputElement;
        inp.setAttribute('aria-label', `Row ${r.row} ${LABELS[f]}`);
        inp.addEventListener('input', () => edit(f, inp.value));
        control = inp;
      }
      return el('td', 'px-0.5 py-1 align-top', {}, control,
        f === 'username' && p && !p.usernameApplied ? el('div', 'text-[11px] text-gold', {}, `not applied: player is ${p.username}`) : null,
        f === 'username' && p?.usernameTaken ? el('div', 'text-[11px] text-gold', {}, 'taken: a suffix is added') : null);
    });
    const main = el('tr', 'border-t border-border/60', {},
      el('td', 'px-1.5 py-1 align-top text-xs text-text-dim', {}, String(r.row),
        p?.newPlayer ? el('div', 'text-[9px] text-gold font-mono', {}, 'NEW') : null),
      ...cells);
    if (!errors.length) return [main];
    return [main, el('tr', '', {},
      el('td'),
      el('td', 'px-1.5 pb-1.5 text-xs text-error', { colSpan: IMPORT_FIELDS.length, dataset: { role: 'row-errors' } }, errors.join(' · ')))];
  }

  function draw() {
    clear(body);
    if (report) {
      const items = new Map(report.items.map((i) => [i.row, i]));
      body.append(
        summary(report),
        el('div', 'max-h-[440px] overflow-auto rounded-lg border border-border', {},
          el('table', 'border-collapse min-w-full', {},
            el('thead', '', {}, el('tr', '', {},
              ...['Row', ...IMPORT_FIELDS.map((f) => LABELS[f])].map((h) =>
                el('th', 'sticky top-0 bg-[#322820] text-left font-normal text-[10px] uppercase tracking-widest text-text-dim px-1.5 py-2 whitespace-nowrap', {}, h)))),
            el('tbody', '', {}, ...rows.flatMap((r) => rowNodes(r, items.get(r.row)))))),
        el('div', 'flex justify-end gap-2 flex-wrap', {}, cancel, recheck, doImport));
    }
    syncButtons();
  }

  draw();
  return el('div', 'game-font rounded-xl border-2 border-[#5a4632] bg-[#322820]/40 p-3 mb-3 flex flex-col gap-3', { dataset: { role: 'import-panel' } },
    el('div', 'text-sm text-[#ffd97a]', {}, 'Import transactions'),
    el('div', 'text-xs text-text-dim', {},
      'Excel (.xlsx) or CSV with the template\'s columns. Rows bind to players by e-mail; an unknown e-mail creates a new '
      + 'player with 0 Shrooms (username blank = the part of the e-mail before @), never e-mailed. Each row becomes a '
      + 'transaction with its invoice. Balances are not changed and no e-mails are sent. Check the rows, edit any cell, '
      + 're-check, then import.'),
    el('div', 'flex flex-wrap items-center gap-2', {},
      btn('↓ TEMPLATE', () => {
        adminApi.importTemplate().then((b) => saveBlob(b, TEMPLATE_FILENAME)).catch((e) => toastError(e, 'Download failed'));
      }),
      file,
      status,
      el('div', 'flex-1'),
      btn('Close', () => onClose())),
    message,
    body);
}
